import { CartCombiDealLineModel } from '@digilize/shared/definitions/src';
import { combineLatest, map, Observable, of } from 'rxjs';

import { CartState } from '../../application/states/cart.state';
import { dealDescription, optionalDeals } from '../../models/cart-item';
import { Product } from '../../models/product';

export interface ConfiguredRow {
  description: string;
  quantity: number;
  total: number;
  /** What adds one more of the same: a variant row's variant, a deal row's lines for the builder. */
  variantId: string | null;
  dealLines: CartCombiDealLineModel[] | null;
  remove: () => Observable<unknown>;
}

/**
 * What is already in the cart for a product that has options: one row per variant, or per distinct
 * deal, each removable on its own — a single counter could not say which one − takes off. Scoped by
 * `parentProductId`: under a ticket only what hangs under a ticket of that product, otherwise only
 * what hangs under none, so an extra and a ticket's supplement of one product never share a row. With a
 * `ticketLineId`, only what hangs under that one ticket. A film extra limited to the ticket quantity hangs
 * under any ticket (PAUL-3884), so it also owns what hangs under a ticket.
 */
export function configuredRows(
  cartState: CartState,
  product$: Observable<Product>,
  parentProductId$: Observable<string | null>,
  ticketLineId$: Observable<string | null> = of(null)
): Observable<ConfiguredRow[]> {
  return combineLatest([
    product$,
    parentProductId$,
    ticketLineId$,
    cartState.items$,
    cartState.combiDeals$,
    cartState.lines$,
    cartState.tickets$,
  ]).pipe(
    map(([product, parentProductId, ticketLineId, items, deals, lines, tickets]): ConfiguredRow[] => {
      const isOurs = (parentLineId: string | null | undefined) =>
        ticketLineId
          ? parentLineId === ticketLineId
          : parentProductId
            ? lines.find((line) => line.id === parentLineId)?.product_id === parentProductId
            : !parentLineId ||
              (!!product.isLimitedToMainProduct && tickets.some((t) => t.reservationId === parentLineId));

      if (product.isCombiDeal) {
        return optionalDeals(deals)
          .filter((deal) => deal.product_id === product.id && isOurs(deal.parent_cart_line_id))
          .map((deal) => ({
            description: dealDescription(deal),
            quantity: deal.quantity,
            total: deal.amount * deal.quantity,
            variantId: null,
            dealLines: deal.lines ?? [],
            remove: () => cartState.removeLine(deal.line_ids[deal.line_ids.length - 1]),
          }));
      }
      return (
        items
          .filter((item) => item.productId === product.id)
          .map((item) => ({ item, own: item.lines.filter((line) => isOurs(line.parent_cart_line_id)) }))
          // A row with no lines of its own cannot be placed under one ticket.
          .filter(({ item, own }) => own.length || (!ticketLineId && !item.lines.length))
          .map(({ item, own }) => ({
            description: item.variantName ?? item.title,
            quantity: own.length === item.lines.length ? item.quantity : own.length,
            total: own.length === item.lines.length ? item.total : item.amount * own.length,
            variantId: item.variantId,
            dealLines: null,
            remove: () =>
              own.length ? cartState.removeLine(own[own.length - 1].id) : cartState.removeItem(item.productId),
          }))
      );
    })
  );
}
